import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faChevronDown, faRotateRight, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { getDomainDisplayName } from '@app/shared/utils/domainDisplay.utils';
import GovernanceTabLoadingState from '../shared/GovernanceTabLoadingState';

export type DomainSensitiveAttribute = {
  setA?: string[];
  setB?: string[];
};

export interface SensitiveAttributesTabProps {
  domain: string;
  attributes: Record<string, DomainSensitiveAttribute> | null;
  isLoading: boolean;
  onRefresh: () => void;
}

const humanizeKey = (key: string): string => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const SensitiveAttributesTab = ({ domain, attributes, isLoading, onRefresh }: SensitiveAttributesTabProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const hasDomain = domain !== 'N/A';
  const domainLabel = hasDomain ? getDomainDisplayName(domain) : 'this project';

  const attributeEntries = useMemo(() => {
    if (!attributes) return [];
    return Object.entries(attributes).map(([key, value]) => ({
      key,
      label: humanizeKey(key),
      setA: value.setA ?? [],
      setB: value.setB ?? []
    }));
  }, [attributes]);

  const filteredEntries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return attributeEntries;
    return attributeEntries.filter(entry => {
      const haystack = [entry.key, entry.label, ...entry.setA, ...entry.setB].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [attributeEntries, searchQuery]);

  if (isLoading) {
    return <GovernanceTabLoadingState label="Loading sensitive attributes..." />;
  }

  if (!hasDomain) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 5,
          px: 2,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'grey.50'
        }}
      >
        <FontAwesomeIcon icon={faShieldHalved} className="text-muted mb-3" size="2x" />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Domain required
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
          Set a project domain in project details to load the sensitive attribute catalog for your industry.
        </Typography>
      </Box>
    );
  }

  if (!attributes || attributeEntries.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 5,
          px: 2,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'grey.50'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          No catalog for {domainLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', mb: 2 }}>
          We could not find sensitive attributes for this domain. Try refreshing or confirm the domain on the project.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FontAwesomeIcon icon={faRotateRight} />}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sensitive attributes available for{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {domainLabel}
        </Box>
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search attributes or terms..."
        value={searchQuery}
        onChange={event => setSearchQuery(event.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="text-muted" />
            </InputAdornment>
          )
        }}
      />

      {filteredEntries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No attributes match &ldquo;{searchQuery.trim()}&rdquo;.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredEntries.map((entry, index) => (
            <Accordion
              key={entry.key}
              defaultExpanded={index === 0}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
                sx={{
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: 1 }
                }}
              >
                <Typography sx={{ fontWeight: 600, flex: '1 1 auto' }}>{entry.label}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2
                  }}
                >
                  <TermGroup
                    title="Set A"
                    tooltip="Primary terms associated with this attribute in detection rules."
                    terms={entry.setA}
                    color="primary"
                  />
                  <TermGroup
                    title="Set B"
                    tooltip="Secondary or related terms grouped with this attribute."
                    terms={entry.setB}
                    color="info"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

function TermGroup({
  title,
  tooltip,
  terms,
  color
}: {
  title: string;
  tooltip: string;
  terms: string[];
  color: 'primary' | 'info';
}) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        bgcolor: color === 'primary' ? 'rgba(13, 110, 253, 0.06)' : 'rgba(13, 202, 240, 0.08)',
        borderColor: color === 'primary' ? 'rgba(13, 110, 253, 0.2)' : 'rgba(13, 202, 240, 0.25)'
      }}
    >
      <Tooltip title={tooltip} arrow>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: `${color}.dark` }}>
          {title}
        </Typography>
      </Tooltip>
      {terms.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No terms configured
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {terms.map(term => (
            <Chip key={term} label={term} size="small" color={color} variant="outlined" />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default SensitiveAttributesTab;
